import { useAuth } from "../../context/AuthContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CalendarClock,
  MessageSquareText,
  PhoneCall,
  Radio,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { formatBatchDateTime, formatBatchSchedule } from "./batch-time";
import { useBatchCallbacks, useBatchContacts, useBatchLiveCalls, useLiveTranscript } from "./queries";
import type { Batch, BatchCallback, BatchContact } from "./types";

type ContactTab = "accepted" | "rejected" | "callback";

/** Same outstanding set as the batch Progress "Callback" tile. */
const PENDING_CALLBACK_STATUSES = new Set([
  "pending",
  "queued",
  "processing",
  "needs_schedule_details",
  "paused",
]);

function isPendingCallback(callback: BatchCallback) {
  const status = String(callback.status || callback.contact_status || "pending")
    .trim()
    .toLowerCase();
  return PENDING_CALLBACK_STATUSES.has(status);
}

/** Contact statuses share the board's colour language, but a contact carries its
 *  own values (queued/failed/cancelled/completed) independent of the batch. */
function contactStatusTone(status?: string) {
  switch (String(status || "").trim().toLowerCase()) {
    case "completed":
      return "bg-success-soft text-success";
    case "failed":
      return "bg-rose-50 text-rose-700";
    case "cancelled":
    case "rejected":
      return "bg-soft text-ink-3";
    case "running":
    case "in_progress":
    case "dialing":
      return "bg-orange-50 text-orange-700";
    default:
      return "bg-amber-50 text-amber-700";
  }
}

function messageTime(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : format(date, "HH:mm:ss");
}

export function BatchDetailsPanel({
  batch,
  onClose,
}: {
  batch: Batch | null;
  onClose: () => void;
}) {
  const { user } = useAuth() as any;
  const globalTz = user?.timezone;
  const [selectedCallId, setSelectedCallId] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [contactTab, setContactTab] = useState<ContactTab | null>(null);
  const shouldPollLive = Boolean(
    batch && (batch.status === "running" || (batch.active_calls_count ?? 0) > 0),
  );
  const contactsQuery = useBatchContacts(
    batch?.schedule_id ?? "",
    Boolean(batch) && (contactTab === "accepted" || contactTab === "rejected"),
  );
  const callbacksQuery = useBatchCallbacks(
    batch?.schedule_id ?? "",
    Boolean(batch) && contactTab === "callback",
  );
  const acceptedContacts = useMemo<BatchContact[]>(
    () => contactsQuery.data?.contacts ?? [],
    [contactsQuery.data],
  );
  const rejectedContacts = useMemo<BatchContact[]>(
    () => contactsQuery.data?.rejected_contacts_data ?? [],
    [contactsQuery.data],
  );
  const callbackContacts = useMemo<BatchCallback[]>(() => {
    const rows = callbacksQuery.data?.items ?? callbacksQuery.data?.callbacks ?? [];
    return rows.filter(isPendingCallback);
  }, [callbacksQuery.data]);

  const filteredAccepted = useMemo(() => {
    const term = contactSearch.trim().toLowerCase();
    if (!term) return acceptedContacts;
    return acceptedContacts.filter((contact) =>
      [contact.name, contact.phone_number, contact.contact_id]
        .some((field) => String(field ?? "").toLowerCase().includes(term)),
    );
  }, [acceptedContacts, contactSearch]);
  const filteredRejected = useMemo(() => {
    const term = contactSearch.trim().toLowerCase();
    if (!term) return rejectedContacts;
    return rejectedContacts.filter((contact) =>
      [contact.name, contact.phone_number, contact.contact_id, contact.rejection_reason]
        .some((field) => String(field ?? "").toLowerCase().includes(term)),
    );
  }, [rejectedContacts, contactSearch]);
  const filteredCallbacks = useMemo(() => {
    const term = contactSearch.trim().toLowerCase();
    if (!term) return callbackContacts;
    return callbackContacts.filter((callback) =>
      [callback.contact_name, callback.customer_phone, callback.contact_id, callback.status]
        .some((field) => String(field ?? "").toLowerCase().includes(term)),
    );
  }, [callbackContacts, contactSearch]);
  const liveCallsQuery = useBatchLiveCalls(batch?.schedule_id ?? "", shouldPollLive);
  const liveCalls = useMemo(
    () => liveCallsQuery.data?.items ?? batch?.active_calls ?? [],
    [batch?.active_calls, liveCallsQuery.data?.items],
  );

  useEffect(() => {
    if (liveCalls.length === 0) {
      setSelectedCallId("");
    } else if (!liveCalls.some((call) => call.call_id === selectedCallId)) {
      setSelectedCallId(liveCalls[0].call_id);
    }
  }, [liveCalls, selectedCallId]);

  // The panel is reused across batches, so a stale search term would otherwise
  // carry over and hide the next batch's contacts.
  useEffect(() => {
    setContactSearch("");
    setContactTab(null);
  }, [batch?.schedule_id]);

  const transcriptQuery = useLiveTranscript(
    batch?.schedule_id ?? "",
    selectedCallId,
    shouldPollLive && Boolean(selectedCallId),
  );
  const selectedCall = liveCalls.find((call) => call.call_id === selectedCallId);

  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = transcriptContainerRef.current;
    if (!el) return;
    // 10px tolerance for "at bottom"
    isAtBottomRef.current = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 10;
  }, []);

  useEffect(() => {
    if (isAtBottomRef.current && transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcriptQuery.data?.messages]);

  if (!batch) return null;

  const answered = batch.accepted ?? 0;
  const rejected = batch.rejected ?? 0;
  const totalFinished = answered + rejected;
  const connectivityRate =
    batch.connectivity != null
      ? batch.connectivity
      : totalFinished > 0
        ? Math.round((answered / totalFinished) * 100)
        : 0;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <aside className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[640px] flex-col border-l border-line bg-canvas shadow-2xl">
        <header className="flex items-start justify-between border-b border-line bg-elevated p-6">
          <div className="min-w-0 pr-8">
            <h2 className="truncate text-xl font-semibold text-ink">{batch.name}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-3">
              <span className="capitalize text-ink-2">{batch.status}</span>
              <span aria-hidden>·</span>
              <span>{batch.agent || "Unassigned agent"}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close batch details"
            className="rounded-lg p-2 text-ink-3 hover:bg-soft"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="custom-scrollbar flex-1 space-y-7 overflow-y-auto p-6">
          {shouldPollLive && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase text-ink-3">
                  <Radio className="h-4 w-4 text-success" />
                  Running calls
                  {liveCalls.length > 0 && (
                    <span className="rounded-full bg-success-soft px-2 py-0.5 text-[10px] text-success">
                      {liveCalls.length} live
                    </span>
                  )}
                </h3>
                {liveCallsQuery.isFetching && (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin text-ink-3" />
                )}
              </div>

              {liveCalls.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-[190px_minmax(0,1fr)]">
                  <div className="max-h-[360px] space-y-1.5 overflow-y-auto">
                    {liveCalls.map((call) => {
                      const name = call.contact_name || call.customer_name || call.contact_id;
                      return (
                        <button
                          key={call.call_id}
                          onClick={() => setSelectedCallId(call.call_id)}
                          className={cn(
                            "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
                            selectedCallId === call.call_id
                              ? "border-success bg-success-soft"
                              : "border-line bg-elevated hover:border-line",
                          )}
                        >
                          <span className="block truncate text-sm font-semibold text-ink">{name}</span>
                          <span className="mt-1 block truncate font-mono text-[10px] text-ink-3">
                            {call.customer_phone || call.contact_id}
                          </span>
                          <span className="mt-1.5 flex items-center gap-1 text-[10px] text-success">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success-soft0" />
                            {call.message_count} messages
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="min-h-[250px] overflow-hidden rounded-lg border border-line bg-elevated">
                    <div className="border-b border-line px-4 py-3">
                      <p className="truncate text-sm font-semibold text-ink">
                        {selectedCall?.contact_name || selectedCall?.customer_name || selectedCall?.contact_id}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[10px] text-ink-3">
                        {selectedCall?.call_id}
                      </p>
                    </div>
                    <div 
                      ref={transcriptContainerRef}
                      onScroll={handleScroll}
                      className="max-h-[430px] space-y-3 overflow-y-auto p-4"
                    >
                      {transcriptQuery.data?.messages.map((message) => (
                        <div key={`${message.index}-${message.item_id ?? ""}`} className="flex gap-3">
                          <span className="w-14 shrink-0 pt-0.5 font-mono text-[10px] text-ink-3">
                            {messageTime(message.created_at)}
                          </span>
                          <div className="min-w-0 text-sm">
                            <span
                              className={cn(
                                "font-semibold",
                                message.role === "assistant" ? "text-orange-600" : "text-ink-2",
                              )}
                            >
                              {message.role === "assistant" ? batch.agent || "Agent" : "Contact"}:
                            </span>{" "}
                            <span className="break-words text-ink-2">{message.text}</span>
                            {message.interrupted && (
                              <span className="ml-2 text-[10px] text-amber-600">Interrupted</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {transcriptQuery.isLoading && (
                        <p className="py-10 text-center text-sm text-ink-3">Loading transcript...</p>
                      )}
                      {!transcriptQuery.isLoading && transcriptQuery.data?.messages.length === 0 && (
                        <div className="py-10 text-center">
                          <MessageSquareText className="mx-auto h-6 w-6 text-line" />
                          <p className="mt-2 text-sm text-ink-3">Waiting for the conversation to begin.</p>
                        </div>
                      )}
                      {transcriptQuery.isError && (
                        <p className="py-10 text-center text-sm text-ink-3">
                          This call has ended. The completed transcript will appear in Call History.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-line bg-elevated px-4 py-8 text-center">
                  <PhoneCall className="mx-auto h-6 w-6 text-line" />
                  <p className="mt-2 text-sm font-medium text-ink-2">Waiting for the first active call</p>
                  <p className="mt-1 text-xs text-ink-3">
                    Running calls and their transcripts will appear here automatically.
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase text-ink-3">Progress</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-line bg-elevated p-4">
                <div className="mb-2 flex items-center gap-2 text-sm text-ink-3">
                  <PhoneCall className="h-4 w-4" /> Total contacts
                </div>
                <div className="text-2xl font-semibold text-ink">{batch.total}</div>
              </div>
              <div className="rounded-lg border border-line bg-elevated p-4">
                <div className="mb-2 flex items-center gap-2 text-sm text-ink-3">
                  <RefreshCw className="h-4 w-4 text-success" /> Completed
                </div>
                <div className="text-2xl font-semibold text-success">{batch.completed}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-4 divide-x divide-slate-200 rounded-lg border border-line bg-elevated p-4 text-center">
              {[
                ["Dialing", batch.running],
                ["Connectivity", `${connectivityRate}%`],
                ["Picked", answered],
                ["Not picked", batch.failed ?? 0],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="mb-1 text-xs text-ink-3">{label}</div>
                  <div className="text-sm font-semibold text-ink">{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase text-ink-3">
                <Users className="h-4 w-4" /> Contacts
              </h3>
              {contactTab && (
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
                  <input
                    type="search"
                    value={contactSearch}
                    onChange={(event) => setContactSearch(event.target.value)}
                    placeholder="Search name or number"
                    aria-label="Search contacts"
                    className="w-52 rounded-lg border border-line bg-elevated py-1.5 pl-8 pr-2.5 text-xs text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </div>
              )}
            </div>

            <div className="mb-3 flex flex-wrap gap-1 rounded-xl bg-slate-100/80 p-1 w-fit border border-slate-200/60">
              {([
                ["accepted", "Accepted", batch.total],
                ["rejected", "Rejected", contactsQuery.isFetched ? rejectedContacts.length : (batch.rejected_contacts ?? 0)],
                ["callback", "Callback", batch.callback],
              ] as const).map(([id, label, count]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setContactTab((current) => (current === id ? null : id))}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                    contactTab === id
                      ? "bg-white text-ink shadow-sm"
                      : "text-ink-3 hover:text-ink",
                  )}
                >
                  {label}
                  <span className="ml-1.5 font-mono text-[10px] text-ink-3">{count}</span>
                </button>
              ))}
            </div>

            {contactTab && (
            <div className="overflow-hidden rounded-lg border border-line bg-elevated">
              {contactTab !== "callback" && contactsQuery.isLoading ? (
                <p className="py-8 text-center text-sm text-ink-3">Loading contacts...</p>
              ) : contactTab !== "callback" && contactsQuery.isError ? (
                <p className="py-8 text-center text-sm text-ink-3">Could not load contacts for this batch.</p>
              ) : contactTab === "callback" && callbacksQuery.isLoading ? (
                <p className="py-8 text-center text-sm text-ink-3">Loading callbacks...</p>
              ) : contactTab === "callback" && callbacksQuery.isError ? (
                <p className="py-8 text-center text-sm text-ink-3">Could not load callbacks for this batch.</p>
              ) : contactTab === "accepted" && acceptedContacts.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-3">No accepted contacts on this batch.</p>
              ) : contactTab === "rejected" && rejectedContacts.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-3">No rejected contacts on this batch.</p>
              ) : contactTab === "callback" && callbackContacts.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-3">No callbacks on this batch.</p>
              ) : contactTab === "accepted" && filteredAccepted.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-3">No accepted contacts match “{contactSearch}”.</p>
              ) : contactTab === "rejected" && filteredRejected.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-3">No rejected contacts match “{contactSearch}”.</p>
              ) : contactTab === "callback" && filteredCallbacks.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-3">No callbacks match “{contactSearch}”.</p>
              ) : contactTab === "callback" ? (
                <div className="max-h-[320px] overflow-y-auto">
                  {filteredCallbacks.map((callback, index) => (
                    <div
                      key={`${callback.call_id ?? callback.contact_id ?? "cb"}-${index}`}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2.5",
                        index > 0 && "border-t border-line",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {callback.contact_name || callback.contact_id || "Unnamed contact"}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-[10px] text-ink-3">
                          {callback.customer_phone || "No number"}
                          {callback.contact_id && ` · ${callback.contact_id}`}
                          {callback.callback_date &&
                            ` · ${callback.callback_date}${callback.callback_time ? ` ${callback.callback_time}` : ""}`}
                        </p>
                      </div>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
                          contactStatusTone(callback.status || callback.contact_status),
                        )}
                      >
                        {callback.status || callback.contact_status || "pending"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="max-h-[320px] overflow-y-auto">
                  {(contactTab === "rejected" ? filteredRejected : filteredAccepted).map((contact, index) => (
                    <div
                      key={`${contact.contact_id ?? contact.phone_number ?? "row"}-${index}`}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2.5",
                        index > 0 && "border-t border-line",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {contact.name || contact.contact_id || "Unnamed contact"}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-[10px] text-ink-3">
                          {contact.phone_number || "No number"}
                          {contact.contact_id && ` · ${contact.contact_id}`}
                          {contactTab === "rejected" && contact.rejection_reason
                            ? ` · ${contact.rejection_reason}`
                            : ""}
                        </p>
                      </div>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
                          contactStatusTone(contact.status),
                        )}
                      >
                        {contactTab === "rejected"
                          ? contact.status || "rejected"
                          : contact.status || "pending"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>

          <div>
            <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-ink-3">
              <CalendarClock className="h-4 w-4" /> Timeline
            </h3>
            <div className="overflow-hidden rounded-lg border border-line bg-elevated text-xs">
              {[
                ["Scheduled", formatBatchSchedule(batch, globalTz)],
                ["Created", formatBatchDateTime(batch.created_at, globalTz)],
                ["Last updated", formatBatchDateTime(batch.updated_at, globalTz)],
                ["Completed", formatBatchDateTime(batch.completed_at, globalTz)],
              ].filter(([, value]) => Boolean(value)).map(([label, value], index) => (
                <div
                  key={label}
                  className={cn(
                    "flex items-start justify-between gap-4 px-3 py-2.5",
                    index > 0 && "border-t border-line",
                  )}
                >
                  <span className="text-ink-3">{label}</span>
                  <span className="max-w-[400px] text-right font-mono font-medium text-ink">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase text-ink-3">Configuration</h3>
            <div className="overflow-hidden rounded-lg border border-line bg-elevated text-xs">
              {[
                ["Concurrency", `${batch.concurrency} parallel calls`],
                ["Agent version", batch.version || "Not available"],
                ["Schedule ID", batch.schedule_id],
              ].map(([label, value], index) => (
                <div
                  key={label}
                  className={cn(
                    "flex items-center justify-between gap-4 px-3 py-2.5",
                    index > 0 && "border-t border-line",
                  )}
                >
                  <span className="text-ink-3">{label}</span>
                  <span className="max-w-[360px] truncate font-mono font-medium text-ink">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {batch.error && (
            <div className="flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              <AlertCircle className="h-5 w-5 shrink-0 text-rose-600" />
              <div>
                <div className="font-medium">Batch failed: {batch.error.code}</div>
                <div className="mt-1">{batch.error.message}</div>
                <div className="mt-2 text-xs text-rose-600">
                  {format(new Date(batch.error.at), "MMM d, yyyy HH:mm:ss")}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
